'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["error", "fontOptions", "onChange", "value", "use"];
import I18n from 'I18n';
import styled from 'foundations-theming/utils';
import PropTypes from 'prop-types';
import { useState, useMemo } from 'react';
import { DropdownCaret } from 'foundations-components/transitional/components/AbstractDropdown';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { Image } from 'foundations-components/transitional/components/Image';
import { MultiLevelDropdown } from 'foundations-components/transitional/components/Dropdown';
import { jsx as _jsx } from "react/jsx-runtime";
const FontVariantOption = styled.span.withConfig({
  displayName: "GroupedFontSelectBody__FontVariantOption",
  componentId: "tkt3a-0"
})(({
  theme
}) => ({
  color: theme.color['text-core-default'],
  fontSize: theme.typography['detail-100'].fontSize
}));
const FontFamilySpan = styled.span.withConfig({
  displayName: "GroupedFontSelectBody__FontFamilySpan",
  componentId: "tkt3a-1"
})(({
  $fontFamily,
  theme
}) => ({
  fontFamily: $fontFamily,
  fontSize: theme.typography['detail-100'].fontSize
}));
const Option = ({
  value,
  help,
  text
}) => {
  if (help) {
    return /*#__PURE__*/_jsx(HelpIcon, {
      title: help,
      tooltipPlacement: "bottom",
      children: text
    });
  } else {
    // @bend-expect trellis-tokenized we need to use the $fontFamily attribute to render the font in its respective fontFamilystyle
    return /*#__PURE__*/_jsx(FontFamilySpan, {
      $fontFamily: value,
      children: text
    });
  }
};
const customFontsValue = fontOptions => {
  const customFonts = fontOptions.find(option => option.text === I18n.text('RichTextLib.formatting.FontSelect.customFontsTitle'));
  if (!customFonts || !customFonts.options) {
    return [];
  }
  return customFonts.options.map(option => option.value);
};
const normalizeFontOptions = fontOptions => {
  return fontOptions.flatMap(option => {
    if (option.options) {
      return normalizeFontOptions(option.options);
    }
    return {
      text: option.text,
      value: option.value
    };
  });
};
const mapFontOptions = (fontOptions, onChange) => {
  return fontOptions.map(option => {
    const isVariant = !!option.variants;
    const mappedOption = Object.assign({}, option, {
      label: /*#__PURE__*/_jsx(Option, {
        value: option.value,
        text: option.text,
        help: option.help
      }),
      onClick: () => {
        onChange(option, isVariant);
      }
    });
    if (option.options) {
      mappedOption.options = mapFontOptions(option.options, onChange);
    } else if (isVariant) {
      mappedOption.options = option.variants.map(variant => {
        const svgUrl = option.svgUrls[variant];
        const variantOption = {
          name: variant,
          fontValue: {
            fallback: option.fallback,
            font_set: option.fontSet || 'CUSTOM',
            font: option.family,
            optionValue: option.value,
            variant,
            styles: Object.assign({}, option.styles[variant])
          },
          svgUrl,
          value: variant,
          label: svgUrl ? /*#__PURE__*/_jsx(Image, {
            src: `${svgUrl}`
          }) : /*#__PURE__*/_jsx(FontVariantOption, {
            children: variant
          })
        };
        variantOption.onClick = () => onChange(variantOption, isVariant);
        return variantOption;
      });
    }
    return mappedOption;
  });
};
function GroupedFontSelectBody(props) {
  const {
      error,
      fontOptions = [],
      onChange,
      value,
      use
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const [open, setOpen] = useState(false);
  const mappedFontOptions = useMemo(() => {
    return mapFontOptions(fontOptions, onChange);
  }, [fontOptions, onChange]);
  const customFontsValues = customFontsValue(fontOptions);
  const normalizedFontOptions = useMemo(() => normalizeFontOptions(fontOptions), [fontOptions]);
  const fontOption = normalizedFontOptions.find(option => option.value === value);
  const currentFontLabel = fontOption ? fontOption.text : I18n.text('RichTextLib.formatting.FontSelect.placeholder');
  return /*#__PURE__*/_jsx(MultiLevelDropdown, Object.assign({}, rest, {
    minInlineSize: "150px",
    maxBlockSize: "228px",
    overflowY: "auto",
    anchorType: "button",
    buttonSize: "small",
    buttonText: currentFontLabel,
    buttonUse: use === 'on-dark' ? 'tertiary-extra-light' : 'tertiary-light',
    caretRenderer: /*#__PURE__*/_jsx(DropdownCaret, {
      buttonUse: "transparent"
    }),
    menuWidth: "auto",
    value: value,
    searchable: false,
    closeOnTargetLeave: true,
    options: mappedFontOptions,
    open: open,
    error: error,
    placeholder: I18n.text('RichTextLib.formatting.FontSelect.placeholder'),
    onChange: evt => {
      const targetValue = evt.target.value;
      if (customFontsValues.includes(targetValue)) {
        console.log('Selecting custom font option', targetValue);
      } else {
        onChange(evt);
        setOpen(false);
      }
    },
    onOpenChange: () => {
      setOpen(!open);
    },
    onSelectedOptionChange: evt => {
      if (evt.target.value && evt.target.value.variants) {
        setOpen(true);
      }
    }
  }));
}
GroupedFontSelectBody.propTypes = {
  error: PropTypes.bool,
  fontOptions: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  TextToolbarSelectComponent: PropTypes.elementType,
  value: PropTypes.string
};
export default GroupedFontSelectBody;